/**
 * Compact single-line address editing helpers for Devices inline Location/Zone edit.
 *
 * Canonical storage remains structured (street / city / state / zip / country).
 * The UI shows one formatted line. Resolve walks from the right (ZIP → state → city)
 * so commas inside street (Suite, Unit, etc.) are not treated as field separators.
 */

export type StructuredAddress = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type AddressResolveResult =
  | { ok: true; address: StructuredAddress }
  | { ok: false; error: string };

const ZIP_RE = /\d{5}(?:-\d{4})?/;

/** Collapse internal whitespace; trim ends. */
export function normalizeAddressLine(line: string): string {
  return line.replace(/\s+/g, " ").trim();
}

/**
 * Format structured fields into one display/edit line.
 * Country is omitted from the line when empty or US (list display typically omits it);
 * resolve preserves baseline country unless the line explicitly ends with a country token.
 */
export function formatStructuredAddress(address: StructuredAddress): string {
  const street = address.street.trim();
  const city = address.city.trim();
  const state = address.state.trim();
  const zip = address.zipCode.trim();
  const country = address.country.trim();

  const core = [street, city, [state, zip].filter(Boolean).join(" ")]
    .filter((part) => part.length > 0)
    .join(", ");

  if (!country || /^US$/i.test(country) || /^USA$/i.test(country)) {
    return core;
  }
  return core ? `${core}, ${country}` : country;
}

function stripOptionalCountry(
  line: string,
  baselineCountry: string
): { rest: string; country: string } {
  const baseline = baselineCountry.trim();
  const candidates = Array.from(
    new Set(
      [baseline, "US", "USA", "United States"]
        .map((c) => c.trim())
        .filter(Boolean)
    )
  );

  for (const candidate of candidates) {
    const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`,\\s*${escaped}\\s*$`, "i");
    if (re.test(line)) {
      return {
        rest: line.replace(re, "").trim(),
        country: baseline || candidate,
      };
    }
  }

  return { rest: line, country: baseline };
}

/**
 * Right-anchored resolve: ZIP at end, then state, then city; remainder is street
 * (may contain commas). Rejects ambiguous / incomplete lines instead of guessing.
 */
export function resolveAddressLine(
  line: string,
  baseline: StructuredAddress
): AddressResolveResult {
  const trimmed = normalizeAddressLine(line);
  if (!trimmed) {
    return {
      ok: false,
      error: "Location name, address, and zone name are required",
    };
  }

  const baselineNorm = normalizeAddressLine(formatStructuredAddress(baseline));
  if (trimmed === baselineNorm) {
    return { ok: true, address: { ...baseline } };
  }

  // Also accept the list's existing formatted string when it matches baseline fields.
  // (Some rows use "City, ST, ZIP" with an extra comma before ZIP.)
  const altBaseline = normalizeAddressLine(
    [
      baseline.street.trim(),
      baseline.city.trim(),
      baseline.state.trim(),
      baseline.zipCode.trim(),
    ]
      .filter(Boolean)
      .join(", ")
  );
  if (trimmed === altBaseline) {
    return { ok: true, address: { ...baseline } };
  }

  const { rest, country } = stripOptionalCountry(trimmed, baseline.country || "US");

  // street, city, ST ZIP   OR   street, city, ST, ZIP
  const anchored =
    /^(.*),\s*([^,]+),\s*([A-Za-z]{2})\s*,?\s*(\d{5}(?:-\d{4})?)$/.exec(rest);

  if (!anchored) {
    return {
      ok: false,
      error:
        "Use format: street, city, state ZIP (street may include Suite/Unit commas)",
    };
  }

  const street = anchored[1].trim();
  const city = anchored[2].trim();
  const state = anchored[3].trim().toUpperCase();
  const zipCode = anchored[4].trim();

  if (!street || !city || !state || !zipCode || !ZIP_RE.test(zipCode)) {
    return {
      ok: false,
      error:
        "Use format: street, city, state ZIP (street may include Suite/Unit commas)",
    };
  }

  return {
    ok: true,
    address: {
      street,
      city,
      state,
      zipCode,
      country: country || baseline.country || "US",
    },
  };
}
